import {useIsConfirmed} from '@hconnect/tuc-processes/fields/isConfirmed/useIsConfirmed'
import {useChangeableField} from '@hconnect/tuc-processes/orderManagement/orderChange'
import {useMemo} from 'react'

import {useRmcCountryFeatures} from '../../shared/order-editor/hooks/useRmcCountryFeatures'
import {getFieldButtonStateProps} from '../utils/getFieldButtonStateProps'

/**
 * Order confirmation UI state types
 */
export type OrderConfirmationUIState =
  | {type: 'hidden'}
  | {type: 'active'; variant: 'default'}
  | {type: 'active'; variant: 'warning'; warningMessage: string}
  | {type: 'disabled'; disabledMessage: string}
  | {type: 'disabled'; variant: 'pin-required'}

/**
 * Hook to determine the UI state for the order confirmation section.
 *
 * Uses useChangeableField and getFieldButtonStateProps to determine the field state,
 * consistent with how other fields (material, PO number, etc.) are handled.
 *
 * Business Rules:
 * - Hidden: canConfirmOrder feature flag is false
 * - Disabled (read-only): field is not changeable according to changeable fields rules
 * - Disabled (pin-required): PIN is required, feature enabled, but not yet validated
 * - Active (warning): field requires dispatch confirmation
 * - Active (default): order can be confirmed without restrictions
 *
 * @returns {OrderConfirmationUIState} The UI state for the confirmation section
 */
export const useOrderConfirmationUIState = (): OrderConfirmationUIState => {
  const regionFeatures = useRmcCountryFeatures()
  const isConfirmedField = useChangeableField({field: 'isConfirmedOrder'})
  const {isReadOnly, requiresApproval} = getFieldButtonStateProps(isConfirmedField)
  const {confirmationPinRequired, canConfirmOrder} = useIsConfirmed()

  return useMemo(() => {
    // Hidden: feature flag disabled
    if (!regionFeatures.canConfirmOrder) {
      return {type: 'hidden'}
    }

    // Disabled: field is not changeable (read-only per changeable fields rules)
    if (isReadOnly) {
      return {
        type: 'disabled',
        disabledMessage: 'orderIntake.rmc.orderManagement.confirmation.contactDispatch'
      }
    }

    // Disabled (PIN required): PIN required by config, not yet validated
    // Note: confirmationPinRequired is already false when PIN has been entered
    if (confirmationPinRequired) {
      return {type: 'disabled', variant: 'pin-required'}
    }

    // Disabled: other reasons (e.g., field not enabled)
    if (!canConfirmOrder) {
      return {
        type: 'disabled',
        disabledMessage: 'orderIntake.rmc.orderManagement.confirmation.contactDispatch'
      }
    }

    // Active (warning): field requires dispatch confirmation
    if (requiresApproval) {
      return {
        type: 'active',
        variant: 'warning',
        warningMessage: 'orderIntake.rmc.orderManagement.confirmation.dispatchRequired'
      }
    }

    // Active (default): no restrictions
    return {type: 'active', variant: 'default'}
  }, [
    regionFeatures.canConfirmOrder,
    isReadOnly,
    confirmationPinRequired,
    canConfirmOrder,
    requiresApproval
  ])
}
